import React, { useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import { Input } from '@/components/ui/input';
import { DragItem } from '@/contexts/DragDropContext';
import { ExpressionGenerator } from '@/lib/expression-generator';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface DroppableInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const DroppableInput = React.forwardRef<HTMLInputElement, DroppableInputProps>(
  ({ value, onChange, onValueChange, onBlur, className, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [cursorPosition, setCursorPosition] = useState<number | null>(null);

    // Handle change events - support both onChange and onValueChange
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      }
      if (onValueChange) {
        onValueChange(e.target.value);
      }
    };

    // Setup drop zone
    const [{ isOver, canDrop }, dropRef] = useDrop<DragItem, void, { isOver: boolean; canDrop: boolean }>({
      accept: 'schema-field',
      drop: (item: DragItem) => {
        // Use centralized expression generator (handles virtual items, path cleanup, etc.)
        const generatedExpression = ExpressionGenerator.generate(item);
        // Add spaces around the expression for readability
        const expression = generatedExpression.replace('{{', '{{ ').replace('}}', ' }}');
        
        // Get current value and cursor position
        const currentValue = value || '';
        const position = cursorPosition ?? currentValue.length;
        
        // Insert expression at cursor position
        const newValue =
          currentValue.slice(0, position) +
          expression +
          currentValue.slice(position);

        // Update the value
        if (onValueChange) {
          onValueChange(newValue);
        } else if (onChange) {
          // Create a synthetic event
          const syntheticEvent = {
            target: { value: newValue },
            currentTarget: { value: newValue },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
        
        // Show success toast
        toast.success(`Inserted: ${item.path}`);
        
        // Focus the input after drop
        setTimeout(() => {
          inputRef.current?.focus();
          // Set cursor position after the inserted expression
          const newPosition = position + expression.length;
          inputRef.current?.setSelectionRange(newPosition, newPosition);
        }, 0);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    });

    // Track cursor position
    const handleSelect = (e: React.SyntheticEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      setCursorPosition(target.selectionStart);
    };

    // Combine refs
    const combinedRef = (node: HTMLInputElement) => {
      // @ts-ignore
      inputRef.current = node;
      dropRef(node);
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    // Wrap onBlur to add logging
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      console.log('🔍 [DroppableInput] Blur event fired, value:', e.target.value, 'hasOnBlur:', !!onBlur);
      if (onBlur) {
        onBlur(e);
      }
    };

    return (
      <div className="relative">
        <Input
          ref={combinedRef}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onSelect={handleSelect}
          onClick={handleSelect}
          className={cn(
            className,
            isOver && canDrop && 'ring-2 ring-primary ring-offset-2',
            canDrop && 'transition-all'
          )}
          {...props}
        />
        {isOver && canDrop && (
          <div className="absolute inset-0 bg-primary/10 pointer-events-none rounded-md border-2 border-primary border-dashed" />
        )}
      </div>
    );
  }
);

DroppableInput.displayName = 'DroppableInput';
